import React, { useEffect, useState } from "react";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { fmtCHF, fmtKM } from "../lib/constants";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Eye, Trash2, ExternalLink, Sparkles, Pencil, Rocket, Crown, Clock, X, TrendingUp, MessageSquare, Award, CreditCard, Loader2 } from "lucide-react";
import SellerStats from "../components/SellerStats";
import SmartBoostBadge from "../components/SmartBoostBadge";

function isBoostActive(c) {
  return !!(c.featured && c.featured_until && new Date(c.featured_until) > new Date());
}
function daysLeft(c) {
  if (!c.featured_until) return 0;
  return Math.max(0, Math.ceil((new Date(c.featured_until) - new Date()) / 86400000));
}
function hoursLeft(c) {
  if (!c.featured_until) return 0;
  return Math.max(0, Math.floor((new Date(c.featured_until) - new Date()) / 3_600_000));
}
function isBoostExpiringSoon(c) {
  if (!isBoostActive(c)) return false;
  const h = hoursLeft(c);
  return h > 0 && h <= 48;
}

/** Banner i18n strings — kept local so we don't touch the translation file. */
const BOOST_BANNER_I18N = {
  de: { eyebrow: "Boost-Erinnerung", oneTitle: (n) => `Dein Boost läuft in ~${n}h ab`,
        manyTitle: (count, n) => `${count} Boosts laufen in ~${n}h ab`, body: "Verlängere jetzt für maximale Sichtbarkeit. Käufer-Klicks brechen sonst nach Ablauf deutlich ein.",
        cta: "Verlängern", dismiss: "Erinnerung ausblenden",
        insightHeading: "Performance-Analyse",
        upliftViews: (pct) => `Dein Boost brachte +${pct}% mehr Views`,
        upliftViewsNeg: (pct) => `${pct >= 0 ? "+" : ""}${pct}% Views vs. Vorperiode`,
        extraInq: (n) => `${n} zusätzliche Anfragen während des Boosts`,
        peerTop: (p) => `Top ${100 - p}% der ähnlichen Inserate`,
        labels: { excellent: "🚀 Sehr stark", good: "📈 Gute Performance", stable: "👍 Stabil", weak: "📊 Wenig Uplift", insufficient: "⚪ Zu wenig Daten" },
        renewHint: "Verlängere, bevor die Sichtbarkeit einbricht.",
        renewHintExcellent: "Nutze den Schwung — verlängere jetzt für maximale Wirkung.",
  },
  fr: { eyebrow: "Rappel boost", oneTitle: (n) => `Votre boost expire dans ~${n}h`,
        manyTitle: (count, n) => `${count} boosts expirent dans ~${n}h`, body: "Prolongez maintenant pour une visibilité maximale.",
        cta: "Prolonger", dismiss: "Masquer le rappel",
        insightHeading: "Analyse de performance",
        upliftViews: (pct) => `Votre boost a apporté +${pct}% de vues`,
        upliftViewsNeg: (pct) => `${pct >= 0 ? "+" : ""}${pct}% vues vs période précédente`,
        extraInq: (n) => `${n} demandes supplémentaires pendant le boost`,
        peerTop: (p) => `Top ${100 - p}% des annonces similaires`,
        labels: { excellent: "🚀 Très fort", good: "📈 Bonne performance", stable: "👍 Stable", weak: "📊 Peu d'effet", insufficient: "⚪ Pas assez de données" },
        renewHint: "Prolongez avant que la visibilité ne chute.",
        renewHintExcellent: "Profitez de l'élan — prolongez maintenant.",
  },
  it: { eyebrow: "Promemoria boost", oneTitle: (n) => `Il tuo boost scade tra ~${n}h`,
        manyTitle: (count, n) => `${count} boost scadono tra ~${n}h`, body: "Rinnova ora per massima visibilità.",
        cta: "Rinnova", dismiss: "Nascondi promemoria",
        insightHeading: "Analisi delle performance",
        upliftViews: (pct) => `Il boost ha portato +${pct}% di visualizzazioni`,
        upliftViewsNeg: (pct) => `${pct >= 0 ? "+" : ""}% visualizzazioni vs periodo precedente`,
        extraInq: (n) => `${n} richieste in più durante il boost`,
        peerTop: (p) => `Top ${100 - p}% degli annunci simili`,
        labels: { excellent: "🚀 Molto forte", good: "📈 Buona performance", stable: "👍 Stabile", weak: "📊 Effetto limitato", insufficient: "⚪ Dati insufficienti" },
        renewHint: "Rinnova prima che la visibilità cali.",
        renewHintExcellent: "Sfrutta lo slancio — rinnova ora.",
  },
  en: { eyebrow: "Boost reminder", oneTitle: (n) => `Your boost expires in ~${n}h`,
        manyTitle: (count, n) => `${count} boosts expire in ~${n}h`, body: "Renew now for maximum visibility.",
        cta: "Renew", dismiss: "Hide reminder",
        insightHeading: "Performance insight",
        upliftViews: (pct) => `Your boost drove +${pct}% more views`,
        upliftViewsNeg: (pct) => `${pct >= 0 ? "+" : ""}${pct}% views vs prior period`,
        extraInq: (n) => `${n} extra inquiries during the boost`,
        peerTop: (p) => `Top ${100 - p}% of similar listings`,
        labels: { excellent: "🚀 Very strong", good: "📈 Good performance", stable: "👍 Stable", weak: "📊 Low uplift", insufficient: "⚪ Not enough data" },
        renewHint: "Renew before visibility drops off.",
        renewHintExcellent: "Ride the momentum — renew now for maximum impact.",
  },
};

/** Tiny 30-px sparkline. Drawn with SVG polyline — no Recharts overhead in the banner. */
function MiniSparkline({ data, color = "#fbbf24", width = 96, height = 28 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => `${i * step},${height - (v / max) * (height - 4) - 2}`).join(" ");
  return (
    <svg width={width} height={height} aria-hidden="true" data-testid="boost-banner-spark">
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PerformanceInsight({ data, lang }) {
  const tr = BOOST_BANNER_I18N[lang] || BOOST_BANNER_I18N.de;
  if (!data || !data.ok) return null;
  const lk = data.performance_label;
  const insufficient = lk === "insufficient";
  const upliftPct = data.views_uplift_pct;
  const extraInq = (data.inquiries_during ?? 0) - (data.inquiries_before ?? 0);
  const labelText = tr.labels[lk] || tr.labels.insufficient;
  const sparkColor = lk === "excellent" || lk === "good" ? "#34d399" : lk === "stable" ? "#fbbf24" : "#a1a1aa";

  return (
    <div className="mt-3 rounded-lg border border-zinc-800/80 bg-black/30 p-3 sm:p-3.5" data-testid="boost-banner-insight">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{tr.insightHeading}</p>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            lk === "excellent" ? "bg-emerald-500/15 text-emerald-300"
            : lk === "good" ? "bg-sky-500/15 text-sky-300"
            : lk === "stable" ? "bg-amber-500/15 text-amber-300"
            : "bg-zinc-700/40 text-zinc-300"
          }`}
          data-testid="boost-banner-insight-label"
        >
          {labelText}
        </span>
      </div>
      {insufficient ? (
        <p className="mt-2 text-[12px] text-zinc-400">{tr.labels.insufficient}</p>
      ) : (
        <div className="mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-[1fr_auto] sm:items-end">
          <div className="space-y-1.5">
            {upliftPct != null && upliftPct >= 10 && (
              <p className="font-display text-base font-semibold text-white sm:text-lg" data-testid="boost-banner-uplift-views">
                {tr.upliftViews(upliftPct)}
              </p>
            )}
            {upliftPct != null && upliftPct < 10 && (
              <p className="font-display text-base font-semibold text-white sm:text-lg" data-testid="boost-banner-uplift-views">
                {tr.upliftViewsNeg(upliftPct)}
              </p>
            )}
            {extraInq > 0 && (
              <p className="flex items-center gap-1.5 text-[12px] text-zinc-300" data-testid="boost-banner-extra-inq">
                <MessageSquare className="h-3 w-3 text-amber-400" /> {tr.extraInq(extraInq)}
              </p>
            )}
            {data.peer_percentile != null && data.peer_percentile >= 50 && (
              <p className="flex items-center gap-1.5 text-[12px] text-zinc-300" data-testid="boost-banner-peer">
                <Award className="h-3 w-3 text-amber-400" /> {tr.peerTop(data.peer_percentile)}
              </p>
            )}
          </div>
          {data.sparkline?.length >= 2 && (
            <div className="flex justify-end self-center">
              <MiniSparkline data={data.sparkline} color={sparkColor} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BoostExpiringBanner({ items, lang, onBoost, boosting }) {
  const [hidden, setHidden] = useState(false);
  const [perf, setPerf] = useState(null); // boost-performance for `first` listing
  const [perfLoaded, setPerfLoaded] = useState(false); // becomes true after fetch resolves (ok or fail)
  const expiring = (items || []).filter(isBoostExpiringSoon);
  expiring.sort((a, b) => hoursLeft(a) - hoursLeft(b));
  const first = expiring[0];
  const firstId = first?.id;

  // Fetch boost-performance for the most-urgent listing only (single API call per banner show).
  useEffect(() => {
    if (!firstId) { setPerf(null); setPerfLoaded(true); return; }
    let alive = true;
    setPerfLoaded(false);
    api.boostPerformance(firstId)
      .then((r) => { if (alive) { setPerf(r); setPerfLoaded(true); } })
      .catch(() => { if (alive) { setPerf(null); setPerfLoaded(true); } });
    return () => { alive = false; };
  }, [firstId]);

  // Fire "shown" tracking exactly once per listing-mount — after perf loads so
  // the analytics event carries the uplift_pct & label (not null/null).
  const trackedRef = React.useRef(new Set());
  useEffect(() => {
    if (!firstId || trackedRef.current.has(firstId)) return;
    if (hidden || !perfLoaded) return;
    trackedRef.current.add(firstId);
    api.boostBannerTrack(firstId, {
      event: "shown",
      uplift_pct: perf?.views_uplift_pct ?? null,
      performance_label: perf?.performance_label ?? null,
    }).catch(() => { /* analytics never blocks UX */ });
  }, [firstId, hidden, perf, perfLoaded]);

  if (hidden || expiring.length === 0) return null;
  const tr = BOOST_BANNER_I18N[lang] || BOOST_BANNER_I18N.de;
  const h = hoursLeft(first);
  const title = expiring.length === 1 ? tr.oneTitle(h) : tr.manyTitle(expiring.length, h);
  const isExcellent = perf?.performance_label === "excellent";
  const renewHint = isExcellent ? tr.renewHintExcellent : tr.renewHint;

  const onCtaClick = () => {
    // Fire-and-forget tracking BEFORE the boost flow takes over.
    api.boostBannerTrack(first.id, {
      event: "cta_clicked",
      uplift_pct: perf?.views_uplift_pct ?? null,
      performance_label: perf?.performance_label ?? null,
    }).catch(() => {});
    onBoost(first);
  };
  const onDismiss = () => {
    setHidden(true);
    api.boostBannerTrack(first.id, {
      event: "dismissed",
      uplift_pct: perf?.views_uplift_pct ?? null,
      performance_label: perf?.performance_label ?? null,
    }).catch(() => {});
  };

  // Premium styling: shift to emerald when perf is excellent (positive reinforcement).
  const accent = isExcellent
    ? "border-emerald-500/30 from-emerald-500/10"
    : "border-amber-500/30 from-amber-500/10";
  const iconColor = isExcellent ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300";
  const eyebrowColor = isExcellent ? "text-emerald-300" : "text-amber-300";
  const ctaColor = isExcellent
    ? "bg-emerald-500 text-zinc-950 hover:bg-emerald-400"
    : "bg-amber-500 text-zinc-950 hover:bg-amber-400";

  return (
    <div
      className={`mt-6 rounded-lg border ${accent} bg-gradient-to-r via-zinc-950 to-zinc-950 p-4 sm:p-5`}
      data-testid="boost-expiring-banner"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${iconColor}`}>
            <Clock className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${eyebrowColor}`}>{tr.eyebrow}</p>
            <p className="mt-0.5 font-display text-base font-semibold text-white sm:text-lg" data-testid="boost-banner-title">{title}</p>
            <p className="mt-0.5 line-clamp-2 text-xs text-zinc-400">
              {renewHint}
              {expiring.length === 1 && first.title && (
                <span className="ml-1 text-zinc-500">· {first.title.slice(0, 60)}</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {expiring.length === 1 && (
            <Button
              size="sm"
              onClick={onCtaClick}
              disabled={boosting === first.id}
              className={ctaColor}
              data-testid="boost-banner-cta"
            >
              <Rocket className="mr-1.5 h-3.5 w-3.5" /> {tr.cta}
            </Button>
          )}
          <button
            type="button"
            onClick={onDismiss}
            aria-label={tr.dismiss}
            className="grid h-7 w-7 place-items-center rounded-md text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300"
            data-testid="boost-banner-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {expiring.length === 1 && <PerformanceInsight data={perf} lang={lang} />}
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [boosting, setBoosting] = useState(null);
  const [statsById, setStatsById] = useState({});       // id → { boost_recommended, boost_reasons }
  const [boostUplift, setBoostUplift] = useState(0);    // global marketplace benchmark
  const [portalBusy, setPortalBusy] = useState(false);  // Phase 57 — Stripe Billing Portal redirect

  // Phase 57 — Open Stripe Customer Portal (Manage subscription / card / invoices / cancel).
  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const { url } = await api.dealerPortalLink(window.location.href);
      if (url) { window.location.href = url; return; }
      throw new Error("no portal url");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Stripe Customer Portal nicht verfügbar";
      toast.error(typeof detail === "string" ? detail : "Stripe Customer Portal nicht verfügbar");
      setPortalBusy(false);
    }
  };

  const reload = () => {
    api.myListings().then(setItems).finally(() => setLoading(false));
    api.mySubscription().then((r) => setSub(r.active));
    // Phase 59 — load dealer profile completion if dealer.
    if (user?.is_dealer) {
      api.dealerMe().then((d) => setDealerProfile(d)).catch(() => setDealerProfile(null));
    }
    // Smart-Boost recommendations (silent — failure is fine)
    api.dashboardStats()
      .then((r) => {
        const map = {};
        (r?.listings || []).forEach((l) => { map[l.id] = l; });
        setStatsById(map);
        setBoostUplift(r?.benchmark?.boost_uplift_factor || 0);
      })
      .catch(() => { /* ignore */ });
  };
  useEffect(() => { reload(); }, []);

  const onDelete = async (id) => {
    try {
      await api.deleteListing(id);
      toast.success("Inserat gelöscht");
      reload();
    } catch { toast.error("Fehler"); }
  };

  const onBoost = async (c) => {
    setBoosting(c.id);
    try {
      const res = await api.createBoostCheckout(c.id);
      if (res?.url) { window.location.href = res.url; return; }
      throw new Error("No checkout url");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (t.boost?.error || "Boost fehlgeschlagen"));
      setBoosting(null);
    }
  };

  // Phase 56 — Premium checkout handler (CHF 19.90 · 30d · TWINT + Card).
  const onPremium = async (c) => {
    setBoosting(`premium-${c.id}`);
    try {
      const res = await api.createPremiumCheckout(c.id);
      if (res?.url) { window.location.href = res.url; return; }
      throw new Error("No checkout url");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Premium fehlgeschlagen");
      setBoosting(null);
    }
  };

  const onCancelBoost = async (c) => {
    setBoosting(c.id);
    try {
      await api.cancelBoost(c.id);
      toast.success("Boost gestoppt");
      reload();
    } catch {
      toast.error(t.boost?.error || "Fehler");
    } finally {
      setBoosting(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">Hi, {user?.name}</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">{t.dash.title}</h1>
        </div>
        <Button asChild className="bg-red-600 text-white hover:bg-red-500">
          <Link to="/sell" data-testid="dash-new"><Plus className="mr-2 h-4 w-4" />{t.dash.new}</Link>
        </Button>
      </div>

      {/* Phase 58 — Trial banner (visible while the dealer subscription is in its free 14-day window). */}
      {sub?.is_trialing && sub?.trial_end && (() => {
        const trialEnd = new Date(sub.trial_end);
        const daysLeft = Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000));
        return (
          <div
            className="mt-8 flex flex-col items-start gap-3 rounded-xl border border-emerald-500/40 bg-gradient-to-r from-emerald-600/10 via-emerald-500/5 to-transparent p-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid="trial-banner"
          >
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-emerald-500/15 text-emerald-300">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-sm font-semibold text-white" data-testid="trial-banner-days">
                  Gratis-Test läuft noch {daysLeft} {daysLeft === 1 ? "Tag" : "Tage"}
                </p>
                <p className="mt-0.5 text-xs text-emerald-200/80">
                  Erste Belastung am {trialEnd.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" })} · jederzeit kündbar
                </p>
              </div>
            </div>
            <Button
              onClick={openBillingPortal}
              disabled={portalBusy}
              size="sm"
              variant="outline"
              className="border-emerald-500/50 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
              data-testid="trial-banner-manage"
            >
              {portalBusy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Öffne Stripe…</> : <><CreditCard className="mr-1.5 h-3.5 w-3.5" /> Abo verwalten</>}
            </Button>
          </div>
        );
      })()}

      {/* Subscription widget */}
      <div className="mt-8 flex flex-col gap-4 rounded-lg border border-zinc-800 bg-zinc-950 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="subscription-widget">
        {sub ? (
          <>
            <div className="flex items-center gap-4">
              <div className="grid h-11 w-11 place-items-center rounded-md bg-red-600/10 text-red-500">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{sub.package_name}</p>
                <p className="mt-0.5 font-display text-lg font-semibold text-white">
                  {sub.credits_left} / {sub.credits_total} {t.pricing.credits} {t.pricing.remaining.toLowerCase()}
                </p>
                <p className="text-xs text-zinc-500">{t.pricing.expires}: {new Date(sub.expires_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Phase 57 — Self-service Stripe Billing Portal for dealer subscriptions. */}
              {sub && ["dealer_basic_monthly", "dealer_pro_monthly"].includes(sub.package) && (
                <Button
                  onClick={openBillingPortal}
                  disabled={portalBusy}
                  variant="outline"
                  className="border-amber-500/40 bg-amber-500/5 text-amber-300 hover:bg-amber-500/10"
                  data-testid="manage-dealer-subscription"
                >
                  {portalBusy ? (
                    <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Öffne Stripe…</>
                  ) : (
                    <><CreditCard className="mr-1.5 h-4 w-4" /> Abo verwalten</>
                  )}
                </Button>
              )}
              <Button asChild variant="outline" className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
                <Link to="/pricing">{t.pricing.choosePlan}</Link>
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t.pricing.noActive}</p>
              <p className="mt-1 text-sm text-zinc-300">{t.pricing.needPlan}</p>
            </div>
            <Button asChild className="bg-red-600 text-white hover:bg-red-500">
              <Link to="/pricing">{t.pricing.choosePlan}</Link>
            </Button>
          </>
        )}
      </div>

      <SellerStats />

      <BoostExpiringBanner items={items} lang={lang} onBoost={onBoost} boosting={boosting} />

      <div className="mt-10">
        <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.dash.myListings}</h2>
        {loading ? (
          <div className="mt-6 h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-950" />
        ) : items.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-zinc-800 p-12 text-center text-zinc-500">{t.dash.noListings}</div>
        ) : (
          <div className="mt-6 divide-y divide-zinc-800 rounded-lg border border-zinc-800 bg-zinc-950">
            {items.map((c) => {
              const boosted = isBoostActive(c);
              // Phase 56 — Premium has its own pill (amber/crown) with priority over Boost.
              const premium = c.premium && c.premium_until && new Date(c.premium_until) > new Date();
              return (
              <div key={c.id} className={`flex flex-col gap-4 p-4 sm:flex-row sm:items-center ${premium ? "bg-gradient-to-r from-amber-950/20 to-transparent" : boosted ? "bg-gradient-to-r from-red-950/15 to-transparent" : ""}`} data-testid={`dash-item-${c.id}`}>
                <div className="relative h-20 w-32 flex-shrink-0 overflow-hidden rounded bg-zinc-900">
                  {c.images?.[0] && <img src={api.fileUrl(c.images[0])} alt="" className="h-full w-full object-cover" />}
                  {premium ? (
                    <span className="absolute left-1 top-1 inline-flex items-center gap-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-black shadow-lg" data-testid={`premium-badge-${c.id}`}>
                      <Crown className="h-2.5 w-2.5" /> PREMIUM
                    </span>
                  ) : boosted ? (
                    <span className="absolute left-1 top-1 inline-flex items-center gap-1 rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-lg" data-testid={`boost-badge-${c.id}`}>
                      <Crown className="h-2.5 w-2.5" />
                    </span>
                  ) : null}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{c.brand} • {c.year}</p>
                    {premium && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300" data-testid={`premium-pill-${c.id}`}>
                        <Crown className="h-2.5 w-2.5" /> Premium · {Math.max(0, Math.ceil((new Date(c.premium_until) - new Date()) / 86400000))} Tage
                      </span>
                    )}
                    {boosted && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-400">
                        <Crown className="h-2.5 w-2.5" /> {t.boost?.active || "Hervorgehoben"} · {daysLeft(c)} {t.boost?.daysLeft || "Tage übrig"}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-1 font-display text-lg font-semibold text-white">{c.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-zinc-400">
                    <span>{fmtCHF(c.price_chf)}</span>
                    <span>{fmtKM(c.mileage_km)} km</span>
                    <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {c.views || 0} {t.dash.views}</span>
                  </div>
                  {!boosted && !premium && statsById[c.id]?.boost_recommended && (
                    <SmartBoostBadge
                      recommended
                      reasons={statsById[c.id]?.boost_reasons || []}
                      upliftFactor={boostUplift}
                      onBoost={() => onBoost(c)}
                      disabled={boosting === c.id}
                      testId={`smart-boost-${c.id}`}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {boosted ? (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => onCancelBoost(c)}
                      disabled={boosting === c.id}
                      className="border-red-500/40 bg-red-500/5 text-red-300 hover:bg-red-500/15"
                      data-testid={`cancel-boost-${c.id}`}
                    >
                      <Crown className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => onBoost(c)}
                      disabled={boosting === c.id}
                      className="border-red-500/40 bg-gradient-to-r from-red-600/15 to-orange-500/10 text-white hover:from-red-600/25 hover:to-orange-500/20"
                      data-testid={`boost-${c.id}`}
                      title={t.boost?.subtitle || "Boost CHF 9.90 / 7 Tage"}
                    >
                      <Rocket className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {/* Phase 56 — Premium upgrade button (only when not already premium). */}
                  {!premium && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => onPremium(c)}
                      disabled={boosting === `premium-${c.id}`}
                      className="border-amber-400/40 bg-gradient-to-r from-amber-500/15 to-yellow-500/10 text-white hover:from-amber-500/25 hover:to-yellow-500/20"
                      data-testid={`premium-${c.id}`}
                      title="Premium CHF 19.90 / 30 Tage"
                    >
                      <Crown className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button asChild variant="outline" size="sm" className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900" data-testid={`view-${c.id}`}>
                    <Link to={`/listings/${c.id}`}><ExternalLink className="h-4 w-4" /></Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900" data-testid={`edit-${c.id}`}>
                    <Link to={`/listings/${c.id}/edit`}><Pencil className="h-4 w-4" /></Link>
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="border-zinc-800 bg-transparent text-red-500 hover:bg-red-950 hover:text-red-400" data-testid={`delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-zinc-800 bg-zinc-950 text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t.dash.confirmDelete}</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">{c.title}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(c.id)} className="bg-red-600 text-white hover:bg-red-500">{t.dash.delete}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
